import axios from "axios";
import { ENV_REACT_APP } from "../Configurations/ENV_Config";
import { getToken } from "../Utils/tokenStorage";

// Contract endpoints are property-scoped file uploads — same axios-direct
// pattern as the J4 owner-photos manager (multipart + Bearer token), not
// RTK Query, so upload progress + polling stay simple and local.
const apiBase = (path: string) =>
  `${ENV_REACT_APP?.REACT_APP_API_BASE_URL || ""}${path}`;

const authHeaders = (): Record<string, string> => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export type Confidence = "high" | "medium" | "low" | "not_found";
export type ExtractionStatus =
  | "pending"
  | "processing"
  | "extracted"
  | "needs_review"
  | "failed";
export type WorkflowStatus =
  | "draft"
  | "vendor_submitted"
  | "homeowner_confirmed"
  | "permit_verified"
  | "simplerchain_ready";

export interface ContractDetail {
  _id: string;
  recordId: string | null;
  tradeType: string | null;
  workflowStatus: WorkflowStatus;
  extractionStatus: ExtractionStatus;
  needsReview: boolean;
  lowConfidenceCount: number;
  extractionError: string | null;
  originalFilename: string;
  mimeType?: string;
  fileSize?: number;
  extractedFields: Record<string, string | null>;
  confidenceScores: Record<string, Confidence>;
  approvedAt: string | null;
  createdAt: string;
  // Serialized by the API but previously missing here. "homeowner" for an
  // upload or a permit claim; "vendor" once vendors can submit (August F5).
  createdByRole?: "homeowner" | "vendor" | "admin";
  // The registered vendor's company name for a vendor submission (Task 5.6) —
  // null for a homeowner's own upload.
  vendorCompanyName?: string | null;
  // F6 (Week 7): this contract came from a bid the homeowner awarded, so it's a
  // job in progress — not a cold "approval needed" submission. When the vendor
  // marks it done, `vendorCompletedAt` is set (a nudge to confirm it).
  awardedFromBid?: boolean;
  vendorCompletedAt?: string | null;
  // Task 9.1: typed per-trade technical specs, display-ready (label/value pairs)
  // — present on a vendor's manually-entered project, null otherwise.
  tradeSpecs?: { label: string; value: string }[] | null;
  // Present on contracts the extractor found priced rows in, and on nothing
  // else — the row regex needs dot-leaders or 2+ spaces before the "$".
  lineItems?: {
    description?: string;
    quantity?: string | null;
    unitCost?: string | null;
    total?: string | null;
  }[];
}

export interface ReconEntry {
  field: string;
  label: string;
  aiValue: string | null;
  dbValue: string | null;
  dbSource: string;
  status: "match" | "conflict" | "db_fill" | "ai_only";
  recommended: "contract" | "database";
}

export interface UploadResult {
  contractId: string;
  extractionStatus: ExtractionStatus;
  workflowStatus: WorkflowStatus;
  originalFilename: string;
}

export type AttachmentKind = "before_photo" | "after_photo" | "invoice" | "warranty_doc";

export interface ContractAttachment {
  _id: string;
  kind: AttachmentKind;
  originalFilename: string | null;
  mimeType: string | null;
  fileSize: number | null;
  uploadedAt: string;
  url: string | null; // short-lived presigned read URL
}

export const ATTACHMENT_KIND_LABELS: Record<AttachmentKind, string> = {
  before_photo: "Before photo",
  after_photo: "After photo",
  invoice: "Invoice",
  warranty_doc: "Warranty doc",
};

export interface ContractDetailResult {
  contract: ContractDetail;
  reconciliation: ReconEntry[];
  fileUrl: string | null;
  attachments?: ContractAttachment[];
}

export const uploadContract = async (
  propertyId: string,
  file: File
): Promise<UploadResult> => {
  const fd = new FormData();
  fd.append("contract", file);
  const res = await axios.post(
    apiBase(`/api/property/${propertyId}/contract`),
    fd,
    { headers: authHeaders() }
  );
  return res.data.data as UploadResult;
};

export const listContracts = async (
  propertyId: string,
  status?: string
): Promise<ContractDetail[]> => {
  const q = status ? `?status=${encodeURIComponent(status)}` : "";
  const res = await axios.get(
    apiBase(`/api/property/${propertyId}/contracts${q}`),
    { headers: authHeaders() }
  );
  return (res.data?.data?.contracts || []) as ContractDetail[];
};

export const getContractDetail = async (
  propertyId: string,
  contractId: string
): Promise<ContractDetailResult> => {
  const res = await axios.get(
    apiBase(`/api/property/${propertyId}/contract/${contractId}`),
    { headers: authHeaders() }
  );
  return res.data.data as ContractDetailResult;
};

export const saveContractDraft = async (
  propertyId: string,
  contractId: string,
  extractedFields: Record<string, string | null>
): Promise<{ contract: ContractDetail }> => {
  const res = await axios.patch(
    apiBase(`/api/property/${propertyId}/contract/${contractId}`),
    { extractedFields },
    { headers: authHeaders() }
  );
  return res.data.data as { contract: ContractDetail };
};

export const approveContract = async (
  propertyId: string,
  contractId: string,
  extractedFields: Record<string, string | null>
): Promise<{ contract: ContractDetail }> => {
  const res = await axios.post(
    apiBase(`/api/property/${propertyId}/contract/${contractId}/approve`),
    { extractedFields },
    { headers: authHeaders() }
  );
  return res.data.data as { contract: ContractDetail };
};

/**
 * Homeowner declines a VENDOR-submitted contract (Task 5.3). Soft on the
 * server: the vendor sees it was declined, but it leaves the homeowner's list.
 */
export const rejectContract = async (
  propertyId: string,
  contractId: string,
  reason?: string
): Promise<{ rejected: boolean; contractId: string }> => {
  const res = await axios.post(
    apiBase(`/api/property/${propertyId}/contract/${contractId}/reject`),
    reason ? { reason } : {},
    { headers: authHeaders() }
  );
  return res.data.data as { rejected: boolean; contractId: string };
};

/**
 * Pulls the server's JSON error message out of a failed blob request.
 *
 * With `responseType: "blob"`, an error body is a Blob rather than parsed JSON,
 * so the usual `e.response.data.errors.message` is unreadable. Read the blob
 * text and parse it; fall back to a friendly default if anything about that
 * fails (non-JSON body, network error with no response, etc.).
 */
const readBlobErrorMessage = async (e: any): Promise<string> => {
  const fallback = "Couldn't generate your report. Please try again.";
  const data = e?.response?.data;
  try {
    if (data && typeof data.text === "function") {
      const parsed = JSON.parse(await data.text());
      return parsed?.errors?.message || parsed?.message || fallback;
    }
  } catch {
    /* non-JSON or unreadable body — use the fallback */
  }
  return fallback;
};

/**
 * GET a PDF endpoint as a blob (with the Bearer token) and hand it to the
 * browser as a save via a temporary object URL — no new tab, no token in a URL.
 *
 * Shared by the report and per-record-certificate downloads. On a non-2xx it
 * reads the blob error body back and throws the server's real message (a blob
 * response makes `e.response.data` a Blob, so `errMsg` alone would only ever see
 * axios's raw "Request failed with status code N").
 */
const downloadPdf = async (path: string, fallbackFilename: string): Promise<void> => {
  let res;
  try {
    res = await axios.get(apiBase(path), { headers: authHeaders(), responseType: "blob" });
  } catch (e: any) {
    throw new Error(await readBlobErrorMessage(e));
  }

  const blob = new Blob([res.data], { type: "application/pdf" });
  // Prefer the server's Content-Disposition filename; fall back to the caller's.
  const disposition = String(res.headers?.["content-disposition"] || "");
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] || fallbackFilename;

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick so the click has started the download first.
  window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

/** The whole-property "Home History Report" PDF. */
export const downloadHomeHistoryReport = (propertyId: string, filenameHint?: string): Promise<void> =>
  downloadPdf(
    `/api/property/${propertyId}/report`,
    `SimplerHome-Report-${filenameHint || "home-history"}.pdf`
  );

/** A single confirmed record's "Certificate of Completion" PDF. */
export const downloadRecordPdf = (propertyId: string, contractId: string): Promise<void> =>
  downloadPdf(
    `/api/property/${propertyId}/contract/${contractId}/pdf`,
    "SimplerHome-Record.pdf"
  );

// Client-side pre-checks (server is the source of truth via magic bytes).
export const CONTRACT_ACCEPT_MIME = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
  "image/webp",
];
export const CONTRACT_ACCEPT_ATTR =
  ".pdf,.docx,image/jpeg,image/png,image/webp,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
export const CONTRACT_MAX_BYTES = 25 * 1024 * 1024; // 25MB (matches server)

// The 10 canonical trade types (must mirror the backend enum).
export const TRADE_TYPES = [
  "water_heater",
  "hvac",
  "roofing",
  "plumbing",
  "electrical",
  "painting",
  "flooring",
  "waterproofing",
  "windows_doors",
  "general_contractor",
];

// Human labels for the trade dropdown.
export const TRADE_LABELS: Record<string, string> = {
  water_heater: "Water Heater",
  hvac: "HVAC",
  roofing: "Roofing",
  plumbing: "Plumbing",
  electrical: "Electrical",
  painting: "Painting",
  flooring: "Flooring",
  waterproofing: "Waterproofing",
  windows_doors: "Windows & Doors",
  general_contractor: "General Contractor",
};

// The 26 review fields grouped for the dialog. Order + membership mirror
// the backend EXTRACTED_FIELD_KEYS.
export const CONTRACT_FIELD_GROUPS: {
  title: string;
  fields: { key: string; label: string; multiline?: boolean; select?: boolean }[];
}[] = [
  {
    title: "Property",
    fields: [
      { key: "address", label: "Property Address" },
      { key: "unitNumber", label: "Unit / Apt" },
      { key: "city", label: "City" },
      { key: "state", label: "State" },
      { key: "zip", label: "ZIP Code" },
    ],
  },
  {
    title: "Owner",
    fields: [
      { key: "ownerName", label: "Owner Name" },
      { key: "ownerEmail", label: "Owner Email" },
    ],
  },
  {
    title: "Contractor",
    fields: [
      { key: "vendorName", label: "Vendor / Company" },
      { key: "vendorLicenseNumber", label: "License #" },
    ],
  },
  {
    title: "Project",
    fields: [
      { key: "tradeType", label: "Trade Type", select: true },
      { key: "projectType", label: "Project Type" },
      { key: "scopeOfWork", label: "Scope of Work", multiline: true },
      { key: "startDate", label: "Start Date" },
      { key: "completionDate", label: "Completion Date" },
      { key: "inspectionDate", label: "Inspection Date" },
    ],
  },
  {
    title: "Cost",
    fields: [
      { key: "totalCost", label: "Total Cost" },
      { key: "laborCost", label: "Labor Cost" },
      { key: "materialCost", label: "Material Cost" },
    ],
  },
  {
    title: "Permit",
    fields: [
      { key: "permitRequired", label: "Permit Required" },
      { key: "permitNumber", label: "Permit #" },
    ],
  },
  {
    title: "Warranty",
    fields: [
      { key: "warrantyYears", label: "Warranty (years)" },
      { key: "warrantyExpiration", label: "Warranty Expiration" },
    ],
  },
  {
    title: "Product",
    fields: [
      { key: "manufacturer", label: "Manufacturer" },
      { key: "modelNumber", label: "Model #" },
      { key: "serialNumber", label: "Serial #" },
      { key: "productDetails", label: "Product Details", multiline: true },
    ],
  },
];

export const errMsg = (e: any, fallback = "Something went wrong."): string =>
  e?.response?.data?.errors?.message ||
  e?.response?.data?.message ||
  e?.message ||
  fallback;
